import styled from '@emotion/styled';
import { useState, useEffect, useMemo } from 'react';
import { useIsMobile, useIsTablet } from '@hooks/responsive/useIsMobile';
import { gray, primary, red } from '@styles/Colors';
import { Time } from '@utils/time';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

// components
import Typography from '@components/_atoms/Typography';
import Loading from '@components/_templates/Loading';
import QuotationFormCard, { QuotationFormType } from '@components/_organism/service/QuotationFormCard';
import ButtonSection from './ButtonSection';
import SubmitMaterialCard from './SubmitMaterialCard';
import TotalCard, { TotalDataType } from '@components/_organism/service/TotalCard';

// constants
import { TypoVariant } from '@constants/atoms';
import { LAPTOP_MODE } from '@constants/size';

// hooks
import { useGetSaleSelfDetailFetch, useGetSaleSelfItemFetch } from '@hooks/fetch/useSaleSelfFetch';
import RequestCard, { UserType } from '@components/_organism/service/RequestCard';
import { useGetSaleDetailFetch } from '@hooks/fetch/useSaleFetch';
import { parseItemDescription } from '@hooks/service/parseItemDescription';
import { parseCalcTotalData } from '@hooks/service/parseCalcTotalData';
import { useGetTotalCardData } from './hooks/useGetTotalCardData';
import { returnOnlyNumber } from '@utils/regex';

const Layout = styled.div``;

const TitleBox = styled.div`
  margin-bottom: 40px;
  text-align: center;

  display: grid;
  justify-content: center;
  row-gap: 8px;
`;

const HightLightRed = styled.span`
  color: ${red.red3};
  font-size: 16px;
`;

const CardListWrapper = styled.div`
  max-width: 1460px;
  min-width: 1080px;
  margin-left: calc((100% - min(1080px, max(700px, 100%))) / 2);

  @media ${LAPTOP_MODE} {
    min-width: 0;
    margin: 0;
  }
`;

const CardList = styled.div`
  display: grid;
  grid-template-columns: 1fr 356px;
  column-gap: 24px;
  align-items: baseline;

  @media ${LAPTOP_MODE} {
    display: flex;
    flex-direction: column-reverse;
    row-gap: 24px;
    align-items: stretch;
  }
`;

const LeftBox = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
`;

const RightBox = styled.div``;

export default function SellerReRequestContent() {
  const { t, i18n } = useTranslation('seller');
  const ko = i18n.language === 'ko';
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();

  const router = useRouter();
  const { rfq_number_id } = router.query as { rfq_number_id?: number };

  const [quotationForm, setQuotationForm] = useState<QuotationFormType>({
    req_close_days: '',
    req_delivery_days: '',
    shipping_area: '',
    terms_of_payment: '',
    additional_request_file: '',
    additional_request_file_name: '',
    seller_note: '',
  });
  const [itemIds, setItemIds] = useState<number[]>([]);

  const { getSaleDetailFetchData: detailData, isCustomLoading: isDetailCustomLoading } = useGetSaleDetailFetch({
    rfq_number_id,
  });
  const { getSaleSelfDetailFetchData: selfDetailData, isCustomLoading: isSelfDetailCustomLoading } =
    useGetSaleSelfDetailFetch({
      rfq_number_id,
    });
  const { getSaleSelfItemFetchData: selfItemData = [], isCustomLoading: isItemCustomLoading } = useGetSaleSelfItemFetch(
    {
      rfq_number_id,
    },
  );
  const { project_name, req_close_at } = detailData ?? {};

  // 견적 상세 내용 setting
  useEffect(() => {
    if (!selfDetailData) return;

    const {
      req_close_terms,
      req_delivery_terms,
      shipping_area,
      terms_of_payment,
      additional_request_file,
      additional_request_file_name,
      seller_note,
    } = selfDetailData ?? {};

    setQuotationForm({
      req_close_days: `${returnOnlyNumber(req_close_terms) || ''}`,
      req_delivery_days: `${returnOnlyNumber(req_delivery_terms) || ''}`,
      shipping_area,
      terms_of_payment,
      additional_request_file,
      additional_request_file_name: additional_request_file_name ?? '',
      seller_note,
    });
  }, [selfDetailData]);

  const totalCardData = useGetTotalCardData(selfItemData, itemIds);
  const requestCardData = {
    ...detailData,
    ...detailData?.expose,
    rfq_number: detailData?.rfq_number?.rfq_number,
  };

  const reqCloseAt = Time(req_close_at).format('YYYY-MM-DD');
  const isCustomLoading = isDetailCustomLoading || isSelfDetailCustomLoading || isItemCustomLoading;
  return (
    <>
      {isCustomLoading ? (
        <Loading isLoading={isCustomLoading} />
      ) : (
        <Layout>
          <TitleBox>
            <Typography style={{ wordBreak: 'break-all' }} variant={TypoVariant.SH1} color={primary.gray}>
              {project_name} <HightLightRed>{t('reRequest.title')}</HightLightRed>
            </Typography>
            <Typography variant={isMobile ? TypoVariant.B4 : TypoVariant.B3} color={gray.gray7}>
              {ko ? <>{reqCloseAt} 마감</> : <>Until {reqCloseAt}</>}
            </Typography>
          </TitleBox>

          <CardListWrapper>
            <CardList>
              {/* only PC */}
              {!isTablet && (
                <LeftBox>
                  <SubmitMaterialCard data={selfItemData} itemIds={itemIds} setItemIds={setItemIds} />
                  <QuotationFormCard quotationForm={quotationForm} setQuotationForm={setQuotationForm} />
                </LeftBox>
              )}
              {/* only PC end */}
              <RightBox>
                <RequestCard data={requestCardData} type={UserType.SELLER} />
              </RightBox>
            </CardList>
          </CardListWrapper>

          {/* Button Section */}
          <ButtonSection rfq_number_id={rfq_number_id} quotationForm={quotationForm} itemIds={itemIds} />
          {/* Button Section end */}
        </Layout>
      )}
    </>
  );
}
